import { useCallback, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { showToast } from '@/utils/toast';
import { storageService } from '@/services/storage';
import { useAuth } from '@/hooks/useAuth';

interface ImageUploadProps {
  value: string;
  onChange: (url: string, filename: string) => void;
}

export function ImageUpload({ value, onChange }: ImageUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const { isAuthenticated } = useAuth();

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      if (!isAuthenticated) {
        throw new Error('Please log in to upload files');
      }

      setIsUploading(true);

      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'];
      if (!validTypes.includes(file.type)) {
        throw new Error('Invalid file type. Please upload JPEG, PNG, WebP, or SVG.');
      }

      // Validate file size (5MB limit)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        throw new Error('File size must be less than 5MB');
      }

      const { url, filename } = await storageService.uploadLogo(file);
      onChange(url, filename);
      
      showToast.success('Logo uploaded successfully');
    } catch (error) {
      console.error('Upload error:', error);
      showToast.error(error instanceof Error ? error.message : 'Failed to upload logo');
    } finally {
      setIsUploading(false);
    }
  }, [onChange, isAuthenticated]);

  const handleRemove = useCallback(async () => {
    try {
      if (value) {
        const filename = value.split('/').pop();
        if (filename) {
          await storageService.deleteLogo(filename);
        }
      }
      onChange('', '');
      showToast.success('Logo removed successfully');
    } catch (error) {
      console.error('Remove error:', error);
      showToast.deleteFailed('logo');
    }
  }, [value, onChange]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <Input
          type="file"
          accept="image/jpeg,image/png,image/webp,image/svg+xml"
          onChange={handleFileChange}
          disabled={isUploading}
          className="max-w-xs"
        />
        {isUploading && <LoadingSpinner size="sm" />}
      </div>

      {value && (
        <div className="relative w-20 h-20">
          <img
            src={value}
            alt="Provider logo"
            className="w-full h-full object-contain rounded border"
          />
          <Button
            size="sm"
            variant="destructive"
            className="absolute -top-2 -right-2 h-6 w-6 p-0"
            onClick={handleRemove}
          >
            ×
          </Button>
        </div>
      )}
    </div>
  );
} 